<script setup lang="ts">
import { ref } from "vue";
import IconComponent from "../common/IconComponent.vue";
import ModalFormTemplate from "../Templates/ModalFormTemplate.vue";
import DataTable from "../../table/DataTable.vue";

withDefaults(
  defineProps<{
    modelValue: string | number | boolean | Date | boolean[] | string[] | number[] | Date[] | null
    endPoint?: string;
    label?: string;
    error?: string | null;
    readonly?: boolean;
    placeholder?: string;
    maxWidth?: string;
    size?: "sm" | "md" | "lg";
  }>(),
  {
    maxWidth: "100%",
    size: "md",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: number | string | null | undefined): void;
  (e: "select", data: any): void;
  (e: "blur"): void;
}>();

const showForm = ref(false);
const handleClick = () => {
  showForm.value = true;
};

const handleInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const value = input.value;

  emit("update:modelValue", value);
};

const handleSelect = () => {
  emit("update:modelValue", "");
};

const focused = ref(false);
const handleBlur = () => {
  focused.value = false;
  emit("blur");
};
</script>

<template>
  <div :style="{ maxWidth: maxWidth, width: '100%' }">
    <label
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
    >
      {{ label }}
    </label>

    <div
      class="relative flex rounded-lg"
      id="button-input"
      :style="{ outline: focused ? '1px solid #e5e7eb' : '' }"
      :class="[
        error
          ? 'border-red-300 dark:border-red-700'
          : 'border-gray-200 dark:border-gray-700',
      ]"
    >
      <!-- Input Field -->
      <input
        type="text"
        :value="modelValue"
        @input="handleInput"
        @focus="focused = true"
        @blur="handleBlur"
        :readonly="true"
        :placeholder="placeholder"
        :style="{ maxWidth: maxWidth, width: '100%' }"
        class="flex-1 px-3 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-center focus:outline-none"
        :class="[
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900',
          size == 'sm' ? 'py-1' : 'py-2',
        ]"
      />

      <!-- handleClick Button -->
      <button
        type="button"
        @click="handleClick"
        @focus="focused = true"
        @blur="focused = false"
        tabindex="-1"
        :disabled="readonly"
        class="flex-none px-3 border border-gray-200 dark:border-gray-700 border-l-0 rounded-r-lg transition-colors"
        :class="[size == 'sm' ? 'py-1' : 'py-2']"
      >
        <IconComponent name="MousePointerClick" />
      </button>
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>

    <ModalFormTemplate
      mode="create"
      width="50rem"
      v-if="showForm"
      :title="'Select ' + label"
      :endPoint="endPoint"
      @save="handleSelect"
      @close="showForm = false"
      :ignore-permission="true"
    >
      <template #form>
        <DataTable
          :title="label ?? ''"
          :fields="[
            { name: 'document_no', caption: 'Ref. No', sortable: true, fieldType: 'text' },
            { name: 'date_time', caption: 'Date', sortable: true, fieldType: 'date' },
            { name: 'vendor', caption: 'Vendor', sortable: true, fieldType: 'text' },
            { name: 'net_total', caption: 'Amount', sortable: true, fieldType: 'number' },
          ]"
          :actions="[
            { name: 'select', permission: 'view', icon: 'MousePointer2', variant: 'primary', title: 'Select', visible: () => true, ignorePermission: true, hideMobile: true,},
          ]"
          :endPoint="endPoint"
          @action="(action, data) => {
            if (action === 'select') {
              emit('update:modelValue', data.document_no);
              emit('select', data);
              showForm = false;
            }
          }"
          @row-dbl-click="(data) => {
            emit('update:modelValue', data.document_no);
            emit('select', data);
            showForm = false;
          }"
          :showEdit="false"
          :showDelete="false"
          :showAdd="false"
          :showSelect="true"
        />
      </template>
    </ModalFormTemplate>
  </div>
</template>
